import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { DollarSign, ShoppingBag, TrendingUp, Package } from "lucide-react";
import LowStockTile from "./LowStockTile";
import QuizStatsTile from "./QuizStatsTile";
import RestockRequestsTile from "./RestockRequestsTile";

const RANGES = [
  { key: "7d", label: "7d" },
  { key: "30d", label: "30d" },
  { key: "90d", label: "90d" },
  { key: "all", label: "All" },
];

const KPI = ({ icon, label, value, color }) => (
  <div className={`brut p-4 ${color}`} data-testid={`kpi-${label.replace(/\s+/g, "-").toLowerCase()}`}>
    <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider">{icon} {label}</div>
    <div className="font-display text-3xl mt-2">{value}</div>
  </div>
);

const fmtUsd = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const shortDay = (iso) => {
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch { return iso; }
};

export default function Analytics() {
  const [rng, setRng] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/analytics/summary", { params: { rng } })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [rng]);

  if (loading || !data) return <div className="brut p-8 mt-6 font-display text-2xl uppercase">Crunching numbers…</div>;

  return (
    <div className="mt-6" data-testid="analytics-tab">
      <div className="flex gap-2 flex-wrap mb-4">
        {RANGES.map((r) => (
          <button key={r.key} onClick={() => setRng(r.key)} className={`brut-btn px-3 py-2 text-sm ${rng === r.key ? "bg-cyan" : "bg-white"}`} data-testid={`range-${r.key}`}>
            {r.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={<DollarSign size={16} strokeWidth={3} />} label="Revenue" value={fmtUsd(data.kpis.total_revenue)} color="bg-yellow" />
        <KPI icon={<ShoppingBag size={16} strokeWidth={3} />} label="Orders" value={data.kpis.orders_count} color="bg-cyan" />
        <KPI icon={<TrendingUp size={16} strokeWidth={3} />} label="Avg Order" value={fmtUsd(data.kpis.avg_order_value)} color="bg-pink text-white" />
        <KPI icon={<Package size={16} strokeWidth={3} />} label="Active Items" value={data.kpis.active_items} color="bg-green" />
      </div>

      <div className="brut p-4 mt-6 bg-white">
        <h3 className="font-display text-2xl uppercase mb-3">Revenue · {rng}</h3>
        {data.revenue_by_day.length === 0 ? (
          <div className="text-center py-10 opacity-60">No sales in this period yet.</div>
        ) : (
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={data.revenue_by_day} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1A1A1A" strokeOpacity={0.15} />
                <XAxis dataKey="date" tickFormatter={shortDay} stroke="#1A1A1A" tick={{ fontSize: 11, fontWeight: 700 }} />
                <YAxis stroke="#1A1A1A" tick={{ fontSize: 11, fontWeight: 700 }} tickFormatter={(v) => `$${v}`} />
                <Tooltip
                  contentStyle={{ border: "3px solid #1A1A1A", borderRadius: 0, boxShadow: "4px 4px 0 0 #1A1A1A", background: "#FBF8F1", fontFamily: "Work Sans" }}
                  formatter={(v) => [fmtUsd(v), "Revenue"]}
                  labelFormatter={shortDay}
                />
                <Bar dataKey="revenue" fill="#FF5A00" stroke="#1A1A1A" strokeWidth={2} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <div className="brut p-4 bg-white">
          <h3 className="font-display text-2xl uppercase mb-3">Best sellers</h3>
          {data.best_sellers.length === 0 ? (
            <div className="text-sm opacity-60">No sales yet.</div>
          ) : (
            <div className="flex flex-col gap-2" data-testid="best-sellers">
              {data.best_sellers.map((s, i) => (
                <div key={s.item_id} className="flex items-center gap-3 border-b-2 border-[#1A1A1A]/10 pb-2 last:border-0">
                  <span className="font-display text-2xl w-7">{i + 1}.</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold truncate">{s.title}</div>
                    <div className="text-xs opacity-70">{s.units} sold · {fmtUsd(s.revenue)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="brut p-4 bg-white">
          <h3 className="font-display text-2xl uppercase mb-3">Coupon usage</h3>
          {data.coupon_usage.length === 0 ? (
            <div className="text-sm opacity-60">No coupons redeemed yet.</div>
          ) : (
            <div className="flex flex-col gap-2" data-testid="coupon-usage">
              {data.coupon_usage.map((c) => (
                <div key={c.code} className="flex items-center justify-between border-b-2 border-[#1A1A1A]/10 pb-2 last:border-0">
                  <div>
                    <div className="font-display text-lg uppercase">{c.code}</div>
                    <div className="text-xs opacity-70">{c.uses} uses</div>
                  </div>
                  <div className="font-bold text-pink">-{fmtUsd(c.total_discount)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <LowStockTile />
      <QuizStatsTile />
      <RestockRequestsTile />
    </div>
  );
}
